/**
 * Per-user avatar colour: the single source of truth for WHICH colour a person
 * gets, shared by every surface that renders an avatar.
 *
 * Why this lives in @overlayqa/shared rather than in each surface: the same
 * person must get the same colour in the extension sidebar, the dashboard and
 * the team-review drawer. Three copies of a hash is three chances to drift, and
 * this repo already paid for that lesson once - `brandLight` was never mirrored
 * into reviewTheme.ts, so three review-app components each pasted
 * `rgba(52, 104, 248, 0.1)` by hand.
 *
 * The colours themselves are design-system tokens on each surface
 * (--oq-avatar-N / tailwind `avatar.N` / reviewTheme `avatar[N]`); this module
 * only decides the SLOT. Keeping the slot here and the colour there is what
 * lets a palette change stay a design-system edit instead of a code change.
 */
/** Number of colours in the avatar palette. Must match the design-system token set. */
export const AVATAR_SLOT_COUNT = 6;
/**
 * FNV-1a, 32-bit. Chosen because it is tiny, dependency-free, and produces the
 * identical result in every JS runtime we ship to (extension service worker,
 * browser, Node) - the cross-surface promise above depends on that.
 *
 * `Math.imul` is load-bearing: a plain `*` overflows into a float past 2^53 and
 * silently stops being FNV-1a.
 */
function fnv1a32(input) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
/**
 * Pick the palette slot for a person.
 *
 * Pass a STABLE identity key - the user id (`IssueComment.authorId`,
 * `member.user.id`), never a display name or email. A name can change, and one
 * person shown as a name on one surface and an email on another would split
 * into two colours, which is exactly the bug this feature exists to avoid.
 *
 * An absent or blank key is not an error: it yields a stable fallback slot, so
 * an unknown author is still consistently coloured rather than flickering.
 */
export function avatarSlot(key) {
    const normalized = (key ?? '').trim().toLowerCase();
    return ((fnv1a32(normalized) % AVATAR_SLOT_COUNT) + 1);
}
/**
 * The canonical avatar palette. Every surface mirrors these exact values into
 * its own design-system format; `pnpm avatar-palette:check` fails if any of
 * them drifts.
 *
 * Chosen by measurement rather than taste, under three constraints that
 * `__tests__/avatarPalette.test.ts` enforces:
 *
 *  1. Every tint/ink pair clears WCAG AA at 4.5:1 (avatar initials are ~10-12px,
 *     so the 3:1 large-text allowance does not apply). Worst pair here is 4.82.
 *  2. The colours are far enough apart to be told apart. This set achieves 33deg
 *     minimum hue separation - the best available from 28 candidate combinations.
 *  3. None of them can be confused with a severity dot, which renders inches
 *     away in every issue list. That reserves the warm end of the wheel
 *     (severity critical 0deg / high 21deg / medium 38deg), which is why there
 *     is no red, orange or amber avatar and never should be.
 *
 * `lime` and `green` are new to the OverlayQA palette (approved 2026-08-31);
 * everything else already existed on at least one surface. Ordered by hue.
 */
export const AVATAR_PALETTE = [
    { name: 'lime', tint: '#ecfccb', ink: '#3f6212' }, // 86deg,  6.52:1
    { name: 'green', tint: '#dcfce7', ink: '#166534' }, // 143deg, 6.49:1
    { name: 'teal', tint: '#ccfbf1', ink: '#115e59' }, // 176deg, 6.73:1
    { name: 'blue', tint: '#dbe6ff', ink: '#2142ab' }, // 226deg, 6.90:1
    { name: 'violet', tint: '#ede9fe', ink: '#6d28d9' }, // 263deg, 5.98:1
    { name: 'pink', tint: '#fee5ef', ink: '#c2205f' }, // 337deg, 4.82:1
];
/**
 * Resolve a person's identity key straight to their colour.
 *
 * This is the function surfaces call. Pass a STABLE id, never a display name -
 * see `avatarSlot` for why.
 */
export function avatarColor(key) {
    return AVATAR_PALETTE[avatarSlot(key) - 1];
}
//# sourceMappingURL=avatarColor.js.map